import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Row, Col, Typography, Tag, Space, Avatar, Button, Table, Select,
  Progress, Breadcrumb, Dropdown, Empty, Spin,
  message, Popconfirm, Tooltip, Input,
} from 'antd';
import useDebounce from '../../../hooks/useDebounce';
import {
  CalendarOutlined, EnvironmentOutlined, MedicineBoxOutlined, BankOutlined,
  TeamOutlined, HomeOutlined, PlusOutlined, DeleteOutlined, EditOutlined,
  CheckCircleOutlined, ClockCircleOutlined, MessageOutlined, PhoneOutlined,
  HeartOutlined, UserOutlined, SearchOutlined, UploadOutlined,
  SyncOutlined, CloseCircleOutlined, SmileOutlined, FileDoneOutlined,
  HourglassOutlined, BookOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { eventApi } from '../../../api/event.api';
import { registrationApi } from '../../../api/registration.api';
import { checkupApi } from '../../../api/checkup.api';
import { childApi } from '../../../api/child.api';
import { smilepassportApi } from '../../../api/smilepassport.api';
import { treatmentApi } from '../../../api/treatment.api';
import { formatDate, resolveFileUrl } from '../../../utils/helpers';
import { usePermission } from '../../../hooks/usePermission';
import AddChildrenModal from '../components/AddChildrenModal';
import EventImportExcelModal from '../components/EventImportExcelModal';
import ChildForm from '../../child/components/ChildForm';

const IMAGES = {
  childrenIllustration: '/assets/images/children-illustration.png',
  dentalChair: '/assets/images/dental-chair.png',

  enrolledChildren: '/assets/images/enrolled-children.png',
  checkupCompleted: '/assets/images/checkup-completed.png',
  awaitingCheckup: '/assets/images/awaiting-checkup.png',
  smilePassport: '/assets/images/smile-passport.png',
  treatment: '/assets/images/dental-chair.png',
};

const { Title, Text } = Typography;

const STANDARD_OPTIONS = [
  'Nursery', 'LKG', 'UKG',
  '1st', '2nd', '3rd', '4th', '5th',
  '6th', '7th', '8th', '9th', '10th',
  '11th', '12th',
];

const computeStatus = (eventDate, storedStatus) => {
  if (storedStatus === 'cancelled')                                 { return 'cancelled'; }
  if (storedStatus === 'completed')                                 { return 'completed'; }
  if (storedStatus === 'ongoing' || storedStatus === 'inprogress') { return 'ongoing'; }
  if (storedStatus === 'upcoming')                                  { return 'upcoming'; }
  const now = dayjs();
  const d   = dayjs(eventDate);
  if (d.isSame(now, 'day'))  { return 'ongoing'; }
  if (d.isAfter(now, 'day')) { return 'upcoming'; }
  return 'completed';
};

const STATUS_CFG = {
  upcoming:  { label: 'Upcoming',  color: 'var(--wl-primary)', bg: 'var(--wl-primary-pale)', icon: <CalendarOutlined /> },
  ongoing:   { label: 'Ongoing',   color: '#52c41a', bg: '#f6ffed', icon: <SyncOutlined /> },
  completed: { label: 'Completed', color: '#595959', bg: '#f5f5f5', icon: <CheckCircleOutlined /> },
  cancelled: { label: 'Cancelled', color: '#ff4d4f', bg: '#fff2f0', icon: <CloseCircleOutlined /> },
};

// Deterministic soft color per initial letter
const avatarColor = (name) => {
  const palette = ['var(--wl-primary-light)','var(--wl-primary)','#00897b','#1e88e5','#e53935','#fb8c00','#43a047','#6d4c41'];
  return palette[(name?.charCodeAt(0) || 65) % palette.length];
};

const EventManage = () => {
  const { id: eventId }   = useParams();
  const navigate          = useNavigate();
  const queryClient       = useQueryClient();
  const { isAdmin, isSchoolStaff, isDoctor } = usePermission();

  const [addModalOpen,    setAddModalOpen]    = useState(false);
  const [addModalTab,     setAddModalTab]     = useState('new');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [childFilter,     setChildFilter]     = useState('all');
  const [childSearch,     setChildSearch]     = useState('');
  const [standardFilter,  setStandardFilter]  = useState('all');
  const [sectionFilter,   setSectionFilter]   = useState('all');
  const [tablePage,     setTablePage]     = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [editModalOpen,   setEditModalOpen]   = useState(false);
  const [editChild,       setEditChild]       = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Dynamic table heading based on selected filter card
  const filterLabels = {
    all:                     'Registered Children',
    checked:                 'Checkups Completed',
    pending:                 'Awaiting Checkup',
    passport:                'Smile Passport Status',
    'passport-published':    'Smile Passport - Published',
    'passport-draft':        'Smile Passport - Draft',
    'passport-notCreated':   'Smile Passport - Not Created',
    // treatment:               'Treatment Status',
    'treatment-published':   'Treatment - Published',
    'treatment-draft':       'Treatment - Draft',
    'treatment-notCreated':  'Treatment - Not Created',
  };

  const handleFilterChange = (key) => {
    if (childFilter === key) {
      setChildFilter('all');
    } else {
      setChildFilter(key);
    }

    setTablePage(1);
    setSelectedRowKeys([]);
  };
  const debouncedChildSearch = useDebounce(childSearch, 250);

  // ── Queries ────────────────────────────────────────────────────
  const { data: eventData, isLoading: eventLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn:  () => eventApi.getById(eventId),
  });

  const { data: regData, isLoading: regLoading } = useQuery({
    queryKey: ['event-registrations', eventId],
    queryFn:  () => registrationApi.getByEvent(eventId),
  });

  const event     = eventData?.data;
  const status    = event ? computeStatus(event.eventDate, event.status) : null;
  const showStats = status === 'completed' && (isAdmin || isDoctor || isSchoolStaff);

  const { data: statsData } = useQuery({
    queryKey: ['event-checkup-stats', eventId],
    queryFn:  () => checkupApi.getEventStats(eventId),
    enabled:  !!event && showStats,
  });

  const { data: checkupsData } = useQuery({
    queryKey: ['event-checkups', eventId],
    queryFn:  () => checkupApi.getAll({ event: eventId, limit: 500 }),
    enabled:  isAdmin || isDoctor,
  });

  const { data: smilepassportsData } = useQuery({
    queryKey: ['event-smilepassports', eventId],
    queryFn:  () => smilepassportApi.getByEvent(eventId),
    enabled:  !!event,
  });

  const { data: treatmentsData } = useQuery({
    queryKey: ['event-treatments', eventId],
    queryFn:  () => treatmentApi.getByEvent(eventId),
    enabled:  !!event,
  });

  // ── Mutations ──────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (regId) => registrationApi.delete(regId),
    onSuccess:  () => {
      queryClient.invalidateQueries(['event-registrations', eventId]);
      queryClient.invalidateQueries(['event', eventId]);
      message.success('Child removed from event');
    },
    onError: (err) => message.error(err.message),
  });

  const updateChildMutation = useMutation({
    mutationFn: ({ id, data }) => childApi.update(id, data),
    onSuccess:  () => {
      queryClient.invalidateQueries(['event-registrations', eventId]);
      message.success('Child details updated');
      setEditModalOpen(false);
      setEditChild(null);
    },
    onError: (err) => {
      if (err?.errorCode !== 'PARENT_ACTION_REQUIRED') { message.error(err.message); }
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => registrationApi.bulkDelete(ids),
    onSuccess:  (res) => {
      queryClient.invalidateQueries(['event-registrations', eventId]);
      queryClient.invalidateQueries(['event', eventId]);
      setSelectedRowKeys([]);
      message.success(`${res.data?.deleted || 0} child(ren) removed from event`);
    },
    onError: (err) => message.error(err.message),
  });

  // ── Derived ────────────────────────────────────────────────────
  const registrations = regData?.data || [];
  const stats         = statsData?.data || null;
  const checkedCount  = registrations.filter((r) => r.isChecked).length;
  const pendingCount  = registrations.length - checkedCount;

  // Calculate smile passport counts
  const smilepassports = smilepassportsData?.data || [];
  const passportPublished = smilepassports.filter((p) => p.status === 'published').length;
  const passportDraft = smilepassports.filter((p) => p.status === 'draft').length;
  const passportNotCreated = registrations.length - smilepassports.length;

  // Calculate treatment counts
  const treatments = treatmentsData?.data || [];
  const treatmentPublished = treatments.filter((t) => t.status === 'published').length;
  const treatmentDraft = treatments.filter((t) => t.status !== 'published').length;
  const treatedChildCount = new Set(
    treatments.map((t) => String(t.child?.id || t.child?._id || t.child || ''))
  ).size;
  const treatmentNotCreated = registrations.length - treatedChildCount;

  const checkupByChildId = useMemo(() => {
    const map = {};
    (checkupsData?.data || []).forEach((c) => {
      const cid = c.child?.id || c.child?._id || String(c.child || '');
      if (cid) { map[cid] = c; }
    });
    return map;
  }, [checkupsData]);

  const passportByChildId = useMemo(() => {
    const map = {};
    smilepassports.forEach((p) => {
      const cid = p.child?.id || p.child?._id || String(p.child || '');
      if (cid) { map[cid] = p; }
    });
    return map;
  }, [smilepassports]);

  const treatmentByChildId = useMemo(() => {
    const map = {};
    treatments.forEach((t) => {
      const cid = t.child?.id || t.child?._id || String(t.child || '');
      if (!cid) return;
      if (!map[cid]) map[cid] = [];
      map[cid].push(t);
    });
    return map;
  }, [treatments]);

  const sectionOptions = useMemo(() => {
    const seen = new Set();
    registrations.forEach(r => {
      const matchesStandard = standardFilter === 'all' || r.child?.standard === standardFilter;
      if (matchesStandard && r.child?.section) seen.add(r.child.section);
    });
    return [...seen].sort();
  }, [registrations, standardFilter]);

  const filteredRows = useMemo(() => {
    let rows = registrations;
    if (childFilter === 'checked') { rows = rows.filter((r) =>  r.isChecked); }
    if (childFilter === 'pending') { rows = rows.filter((r) => !r.isChecked); }
    if (childFilter === 'passport-published') {
      rows = rows.filter((r) => {
        const childId = r.child?.id || r.child?._id;
        return passportByChildId[String(childId || '')]?.status === 'published';
      });
    }
    if (childFilter === 'passport-draft') {
      rows = rows.filter((r) => {
        const childId = r.child?.id || r.child?._id;
        return passportByChildId[String(childId || '')]?.status === 'draft';
      });
    }
    if (childFilter === 'passport-notCreated') {
      rows = rows.filter((r) => {
        const childId = r.child?.id || r.child?._id;
        return !passportByChildId[String(childId || '')];
      });
    }
    if (childFilter === 'treatment-published') {
      rows = rows.filter((r) => {
        const childId = r.child?.id || r.child?._id;
        return (treatmentByChildId[String(childId || '')] || []).some((t) => t.status === 'published');
      });
    }
    if (childFilter === 'treatment-draft') {
      rows = rows.filter((r) => {
        const childId = r.child?.id || r.child?._id;
        const records = treatmentByChildId[String(childId || '')] || [];
        return records.length > 0 && !records.some((t) => t.status === 'published');
      });
    }
    if (childFilter === 'treatment-notCreated') {
      rows = rows.filter((r) => {
        const childId = r.child?.id || r.child?._id;
        return !(treatmentByChildId[String(childId || '')] || []).length;
      });
    }
    if (standardFilter && standardFilter !== 'all') { rows = rows.filter((r) => r.child?.standard === standardFilter); }
    if (sectionFilter && sectionFilter !== 'all')  { rows = rows.filter((r) => r.child?.section  === sectionFilter); }
    if (debouncedChildSearch) {
      const q = debouncedChildSearch.toLowerCase();
      rows = rows.filter((r) =>
        r.child?.name?.toLowerCase().includes(q) ||
        r.child?.parent?.name?.toLowerCase().includes(q) ||
        String(r.child?.parent?.phone || '').includes(q)
      );
    }
    return rows;
  }, [registrations, childFilter, debouncedChildSearch, passportByChildId, treatmentByChildId, standardFilter, sectionFilter]);

  // ── Early returns ──────────────────────────────────────────────
  if (eventLoading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }
  if (!event) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Text type="secondary">Event not found.</Text><br />
        <Button style={{ marginTop: 12 }} onClick={() => navigate('/events')}>Back to Events</Button>
      </div>
    );
  }

  // ── Remaining derived ──────────────────────────────────────────
  const cfg        = STATUS_CFG[status] || STATUS_CFG.upcoming;
  const doctors    = event.assignedDoctors || [];
  const canManage  = (isAdmin || isSchoolStaff || isDoctor) && status !== 'completed' && status !== 'cancelled';
  const canEdit    = isAdmin || isSchoolStaff;
  const canCheckup = isAdmin || isDoctor;

  const addMenuItems = [
    { key: 'new',    label: '+ Add New Child' },
    { key: 'assign', label: '🔍 Search & Assign' },
  ];
  const handleAddMenu    = ({ key }) => { setAddModalTab(key); setAddModalOpen(true); };
  const handleAddSuccess = () => {
    queryClient.invalidateQueries(['event-registrations', eventId]);
    queryClient.invalidateQueries(['event', eventId]);
    queryClient.invalidateQueries(['children']);
  };

  // ── Stat cards config ──────────────────────────────────────────
  const statCards = [
    {
      key: 'all',
      label: 'Enrolled Children',
      value: registrations.length,
      desc: 'Total children registered',
      color: 'var(--wl-primary)',
      bg: 'var(--wl-primary-pale)',
      image: IMAGES.enrolledChildren,
    },
    {
      key: 'pending',
      label: 'Awaiting Checkup',
      value: pendingCount,
      desc: 'Children yet to complete checkup',
      color: '#fa8c16',
      bg: '#fff7e6',
      image: IMAGES.awaitingCheckup,
    },
    {
      key: 'checked',
      label: 'Checkups Completed',
      value: checkedCount,
      desc: 'Children who completed checkup',
      color: '#52c41a',
      bg: '#f0fff4',
      image: IMAGES.checkupCompleted,
    },
    {
      key: 'passport',
      label: 'Smile Passport Status',
      color: 'var(--wl-primary)',
      bg: 'var(--wl-primary-pale)',
      image: IMAGES.smilePassport,

      published: passportPublished,
      draft: passportDraft,
      notCreated: passportNotCreated,
    },
    // {
    //   key: 'treatment',
    //   label: 'Treatment Status',
    //   color: '#e53935',
    //   bg: '#fff1f0',
    //   image: IMAGES.treatment,

    //   published: treatmentPublished,
    //   draft: treatmentDraft,
    //   notCreated: treatmentNotCreated,
    // },
  ];

  // ── Table columns ──────────────────────────────────────────────
  const columns = [
    {
      title: '#',
      width: 44,
      render: (_, __, i) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {(tablePage - 1) * tablePageSize + i + 1}
        </Text>
      ),
    },
    {
      title: 'Child Name',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar
            size={34}
            src={r.child?.childImage ? resolveFileUrl(r.child.childImage) : undefined}
            style={{
              background: r.child?.childImage ? undefined : avatarColor(r.child?.name),
              flexShrink: 0, fontWeight: 700, color: '#fff',
            }}
          >
            {!r.child?.childImage && r.child?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 13 }}>{r.child?.name || '—'}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Age / Gender',
      render: (_, r) => (
        <div>
          <Text style={{ fontSize: 13, fontWeight: 600 }}>
            {r.child?.age != null ? `${r.child.age} yrs` : '—'}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: 11, display: 'block', textTransform: 'capitalize' }}
          >
            {r.child?.gender || '—'}
          </Text>
        </div>
      ),
    },
    {
      title: isSchoolStaff ? 'Parent / Phone' : 'Parent',
      render: (_, r) => (
        <div>
          <Text style={{ fontSize: 12 }}>{r.child?.parent?.name || '—'}</Text>
          {isSchoolStaff && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <PhoneOutlined style={{ color: 'var(--wl-primary)', fontSize: 11 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {r.child?.parent?.phone || '—'}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Registered On',
      render: (_, r) => {
        const d = r.registrationDate ? dayjs(r.registrationDate) : null;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <CalendarOutlined style={{ color: 'var(--wl-primary)', fontSize: 11 }} />
              <Text style={{ fontSize: 12 }}>{d ? d.format('DD MMM YYYY') : '—'}</Text>
            </div>
            {d && (
              <Text type="secondary" style={{ fontSize: 11, paddingLeft: 16 }}>
                {d.format('hh:mm A')}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      width: 140,
      render: (_, r) => {
        const childId  = r.child?.id || r.child?._id;
        const passport = passportByChildId[String(childId || '')];
        if (passport?.status === 'published') {
          return (
            <Tag icon={<SmileOutlined />} style={{ borderRadius: 9999, background: '#f0f5ff', color: 'var(--wl-primary)', border: '1px solid var(--wl-primary-light)', fontWeight: 600 }}>
              Published
            </Tag>
          );
        }
        if (passport?.status === 'draft') {
          return (
            <Tag icon={<SmileOutlined />} style={{ borderRadius: 9999, background: '#f9f0ff', color: '#722ed1', border: '1px solid #d3adf7', fontWeight: 600 }}>
              Draft
            </Tag>
          );
        }
        if (r.isChecked) {
          return (
            <Tag icon={<CheckCircleOutlined />} style={{ borderRadius: 9999, background: '#f0fff4', color: '#52c41a', border: '1px solid #b7eb8f', fontWeight: 600 }}>
              Checked
            </Tag>
          );
        }
        return (
          <Tag icon={<ClockCircleOutlined />} style={{ borderRadius: 9999, background: '#fff7e6', color: '#fa8c16', border: '1px solid #ffd591', fontWeight: 600 }}>
            Awaiting
          </Tag>
        );
      },
    },
  ];

  if (canEdit || canManage || canCheckup) {
    columns.push({
      title:  'Actions',
      key:    'actions',
      width:  110,
      render: (_, r) => {
        const childId         = r.child?.id || r.child?._id;
        const existingCheckup = checkupByChildId[String(childId || '')];
        return (
          <Space size={6}>
            {/* Smilepassport — only for checked children */}
            {/* {r.isChecked && (
              <Tooltip title="Smile Passport">
                <Button
                  type="text"
                  size="small"
                  icon={<SmileOutlined style={{ color: '#90f45e' }} />}
                  onClick={() => {
                    const childIdValue = r.child?.id || r.child?._id;
                    navigate(`/events/${eventId}/smilepassport/${childIdValue}`);
                  }}
                />
              </Tooltip>
            )} */}
            {canCheckup && (
              <Tooltip title={existingCheckup ? 'Update checkup' : 'View Details'}>
                <Button
                  shape="circle"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
                  onClick={() => navigate(`/events/${eventId}/children/${r.id || r._id}`)}
                />
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Edit child">
                <Button
                  shape="circle"
                  size="small"
                  icon={<EditOutlined />}
                  style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
                  onClick={() => { setEditChild(r.child); setEditModalOpen(true); }}
                />
              </Tooltip>
            )}
            {canManage && !r.isChecked && (
              <Popconfirm
                title="Remove this child from the event?"
                onConfirm={() => deleteMutation.mutate(r.id || r._id)}
                okText="Remove"
                okButtonProps={{ danger: true }}
                placement="topRight"
              >
                <Button
                  shape="circle"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
                />
              </Popconfirm>
            )}
          </Space>
        );
      },
    });
  }

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'default' }} /> },
          { title: <span onClick={() => navigate('/events')} style={{ cursor: 'default', color: 'var(--wl-primary)' }}>Events</span> },
          {
            title: (
              <Tooltip title={event.title.length > 15 ? event.title : undefined}>
                <span>{event.title.length > 15 ? `${event.title.slice(0, 15)}...` : event.title}</span>
              </Tooltip>
            ),
          },
        ]}
      />

      {/* ── Event header ──────────────────────────────────────── */}
      <Card
        style={{
          borderRadius: 20,
          border:       '1px solid var(--wl-primary-pale)',
          background:   'linear-gradient(120deg, var(--wl-primary-pale) 0%, #ffffff 50%, #eef3ff 100%)',
          boxShadow:    '0 4px 24px rgba(var(--wl-primary-rgb),0.08)',
          marginBottom: 24,
          overflow:     'hidden',
        }}
        styles={{ body: { padding: '12px 18px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>

          {/* ── Left section: illustration + event info ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, paddingRight: 20 }}>

            {/* Illustration circle */}
            <div style={{
              width:          64,
              height:         64,
              borderRadius:   '50%',
              background:     'linear-gradient(145deg, var(--wl-primary-pale), var(--wl-primary-pale))',
              flexShrink:     0,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              boxShadow:      '0 4px 14px rgba(var(--wl-primary-rgb),0.20)',
              position:       'relative',
              overflow:       'hidden',
            }}>
              <div style={{ position: 'absolute', width: 46, height: 46, borderRadius: '50%', background: 'rgba(255,255,255,0.25)' }} />
              <img
                src={IMAGES.childrenIllustration}
                alt="Children"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>

            {/* Event info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Status tag */}
              <div style={{ marginBottom: 4 }}>
                <Tag
                  icon={cfg.icon}
                  style={{
                    background:   cfg.bg,
                    color:        cfg.color,
                    border:       `1.5px solid ${cfg.color}55`,
                    borderRadius: 9999,
                    fontWeight:   700,
                    fontSize:     10,
                    padding:      '1px 10px',
                    display:      'inline-flex',
                    alignItems:   'center',
                    gap:          4,
                    marginBottom: 0,
                  }}
                >
                  {cfg.label}
                </Tag>
              </div>
              {/* Title */}
              <Title
                level={4}
                ellipsis={{ tooltip: event.title, rows: 2 }}
                style={{ margin: '0 0 6px', color: 'var(--wl-text)', lineHeight: 1.25, fontWeight: 700, fontSize: 16 }}
              >
                {event.title}
              </Title>
              {/* Meta row */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 4, columnGap: 0 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <CalendarOutlined style={{ color: 'var(--wl-primary-light)', fontSize: 11 }} />
                  <Text style={{ fontSize: 12, color: '#444' }}>{formatDate(event.eventDate, 'DD MMM YYYY')}</Text>
                </span>
                {event.location && (
                  <>
                    <span style={{ color: 'var(--wl-primary-pale)', margin: '0 8px', fontSize: 12, lineHeight: 1 }}>|</span>
                    <Tooltip title={event.location.length > 40 ? event.location : undefined}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 200, overflow: 'hidden' }}>
                        <EnvironmentOutlined style={{ color: 'var(--wl-primary-light)', fontSize: 11, flexShrink: 0 }} />
                        <Text style={{ fontSize: 12, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {event.location}
                        </Text>
                      </span>
                    </Tooltip>
                  </>
                )}
                {event.school?.name && (
                  <>
                    <span style={{ color: 'var(--wl-primary-pale)', margin: '0 8px', fontSize: 12, lineHeight: 1 }}>|</span>
                    <Tooltip title={event.school.name.length > 40 ? event.school.name : undefined}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 200, overflow: 'hidden' }}>
                        <BankOutlined style={{ color: 'var(--wl-primary-light)', fontSize: 11, flexShrink: 0 }} />
                        <Text style={{ fontSize: 12, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {event.school.name}
                        </Text>
                      </span>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>

          </div>

          {/* ── Gradient vertical divider ── */}
          <div style={{
            width:      1,
            alignSelf:  'stretch',
            background: 'linear-gradient(to bottom, transparent 0%, var(--wl-primary-pale) 18%, var(--wl-primary-pale) 82%, transparent 100%)',
            flexShrink: 0,
            margin:     '0 4px',
          }} />

          {/* ── Right section: dental image + doctors ── */}
          <div style={{
            flexShrink:     0,
            display:        'flex',
            flexDirection:  'column',
            alignItems:     'center',
            justifyContent: 'center',
            gap:            8,
            paddingLeft:    20,
          }}>
            <div style={{ width: 160, height: 86, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={IMAGES.dentalChair}
                alt="Dental Chair"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <div style={{ textAlign: 'center' }}>
              {doctors.length > 0 ? (
                <>
                  <Avatar.Group
                    maxCount={3}
                    size={28}
                    maxStyle={{ background: 'var(--wl-primary-light)', color: '#fff', fontWeight: 700, fontSize: 11 }}
                  >
                    {doctors.map((d) => (
                      <Tooltip key={d.id || d._id} title={d.name}>
                        <Avatar
                          size={28}
                          style={{ background: avatarColor(d.name), fontWeight: 700, fontSize: 12, border: '2px solid #fff' }}
                        >
                          {d.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </Avatar.Group>
                  <Text style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#555', fontWeight: 600 }}>
                    {doctors.length} Doctors Assigned
                  </Text>
                </>
              ) : (
                <>
                  <Avatar size={28} style={{ background: 'var(--wl-primary-pale)', color: '#aaa' }} icon={<TeamOutlined />} />
                  <Text style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#aaa' }}>No doctors assigned</Text>
                </>
              )}
            </div>
          </div>

        </div>
      </Card>

      {/* ── Stat cards ────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statCards.map(({
          key,
          label,
          value,
          desc,
          color,
          bg,
          image,
          published,
          draft,
          notCreated,
        }) => {
          const isFilter = ['all', 'checked', 'pending', 'passport', 'treatment'].includes(key);
          const isBreakdownCard = key === 'passport' || key === 'treatment';
          const active = childFilter === key || (isBreakdownCard && childFilter?.startsWith(`${key}-`));
          return (
            <Col xs={24} sm={12} lg={6} key={key}>
              <Card
                onClick={() => {
                  if (isFilter && !isBreakdownCard) {
                    handleFilterChange(key);
                  }
                }}
                hoverable={isFilter && !isBreakdownCard}
                style={{
                  borderRadius: 18,
                  border: active
                    ? `2px solid ${color}`
                    : '1.5px solid var(--wl-primary-pale)',
                  boxShadow: active
                    ? `0 6px 24px ${color}40, inset 0 0 0 1px ${color}15`
                    : '0 2px 12px rgba(var(--wl-primary-rgb),0.06)',
                  cursor: isFilter && !isBreakdownCard ? 'pointer' : 'default',
                  background: active ? `${color}06` : '#fff',
                  transition: 'all .2s ease',
                  height: '100%',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                styles={{ body: { padding: '22px 24px', height: '100%' } }}
              >
                {/* Top accent bar when active */}
                {active && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${color}, ${color}88)`,
                    borderRadius: '18px 18px 0 0',
                  }} />
                )}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
                  <div style={{
                    width:          60,
                    height:         60,
                    borderRadius:   16,
                    background:     active ? `${color}20` : bg,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    flexShrink:     0,
                    transition:     'background .2s ease',
                  }}>
                   <img
                    src={image}
                    alt={label}
                    style={{
                      width: 42,
                      height: 42,
                      objectFit: 'contain',
                    }}
                  />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text style={{ fontSize: 13, color: active ? color : '#888', display: 'block', marginBottom: 4, fontWeight: active ? 700 : 400 }}>
                      {label}
                    </Text>
                    {isBreakdownCard ? (
                      <div style={{ marginTop: 4 }}>
                        <div
                          onClick={() => handleFilterChange(`${key}-published`)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: 4,
                            background: childFilter === `${key}-published` ? '#f0fff4' : 'transparent',
                            transition: 'background .2s ease',
                          }}
                        >
                          <Text style={{ fontSize: 12, fontWeight: childFilter === `${key}-published` ? 600 : 400 }}>Published</Text>
                          <Text strong style={{ color: '#52c41a' }}>{published}</Text>
                        </div>
                        <div
                          onClick={() => handleFilterChange(`${key}-draft`)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: 4,
                            background: childFilter === `${key}-draft` ? '#f9f0ff' : 'transparent',
                            transition: 'background .2s ease',
                          }}
                        >
                          <Text style={{ fontSize: 12, fontWeight: childFilter === `${key}-draft` ? 600 : 400 }}>Draft</Text>
                          <Text strong style={{ color: '#faad14' }}>{draft}</Text>
                        </div>
                        <div
                          onClick={() => handleFilterChange(`${key}-notCreated`)}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: 4,
                            background: childFilter === `${key}-notCreated` ? '#fff2f0' : 'transparent',
                            transition: 'background .2s ease',
                          }}
                        >
                          <Text style={{ fontSize: 12, fontWeight: childFilter === `${key}-notCreated` ? 600 : 400 }}>Not Created</Text>
                          <Text strong style={{ color: '#ff4d4f' }}>{notCreated}</Text>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 38, fontWeight: 800, color, lineHeight: 1.1 }}>
                        {value}
                      </div>
                    )}
                    <Text style={{ fontSize: 11, color: '#b0b0b0', display: 'block', marginTop: 4, lineHeight: 1.4 }}>
                      {desc}
                    </Text>
                  </div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* ── Dental health summary (completed events only) ─────── */}
      {showStats && stats && stats.totalChecked > 0 && (
        <Card
          title={
            <Space>
              <HeartOutlined style={{ color: '#ff4d4f' }} />
              <span style={{ fontWeight: 700 }}>Dental Health Summary</span>
            </Space>
          }
          style={{
            borderRadius: 16,
            border:       '1px solid #ffd6d6',
            background:   '#fffafa',
            marginBottom: 24,
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <div>
                <Text style={{ fontSize: 12, color: '#888' }}>Total Checked</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--wl-primary)' }}>{stats.totalChecked}</div>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div>
                <Text style={{ fontSize: 12, color: '#888' }}>Need Treatment</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#fa8c16' }}>
                  {stats.treatmentRequired || 0}
                  <Text style={{ fontSize: 12, color: '#aaa', marginLeft: 4 }}>/ {stats.totalChecked}</Text>
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div>
                <Text style={{ fontSize: 12, color: '#888' }}>Cavities Found</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#f5222d' }}>{stats.hasCavities || 0}</div>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div>
                <Text style={{ fontSize: 12, color: '#888' }}>Gum Issues</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#fa541c' }}>{stats.hasGumIssues || 0}</div>
              </div>
            </Col>
          </Row>
          <div style={{ marginTop: 20 }}>
            <Text style={{ fontSize: 12, color: '#888', marginBottom: 6, display: 'block' }}>
              Treatment required rate
            </Text>
            <Progress
              percent={Math.round(((stats.treatmentRequired || 0) / stats.totalChecked) * 100)}
              strokeColor="#fa8c16"
              trailColor="#fff3e0"
              format={(p) => `${p}% need treatment`}
            />
          </div>
        </Card>
      )}

      {/* ── Children table ─────────────────────────────────────── */}
      <Card
        style={{
          borderRadius: 18,
          border:       '1.5px solid var(--wl-primary-pale)',
          boxShadow:    '0 2px 16px rgba(var(--wl-primary-rgb),0.07)',
        }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Table header */}
        <div style={{
          padding:      '18px 24px',
          borderBottom: '1px solid var(--wl-primary-pale)',
        }}>
          <Space size={8}>
            <UserOutlined style={{ color: 'var(--wl-primary)', fontSize: 16 }} />
            <Text strong style={{ fontSize: 16, color: 'var(--wl-text)' }}>{filterLabels[childFilter] || 'Registered Children'}</Text>
          </Space>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
          <Select
            placeholder="Standard"
            value={standardFilter}
            onChange={(val) => { setStandardFilter(val || 'all'); setSectionFilter('all'); setTablePage(1); setSelectedRowKeys([]); }}
            allowClear={standardFilter !== 'all'}
            onClear={() => { setStandardFilter('all'); setSectionFilter('all'); setTablePage(1); setSelectedRowKeys([]); }}
            clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
            size="small"
            style={{ width: 140 }}
          >
            <Select.Option key="all" value="all">Standard: All</Select.Option>
            {STANDARD_OPTIONS.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Section"
            value={sectionFilter}
            onChange={(val) => { setSectionFilter(val || 'all'); setTablePage(1); setSelectedRowKeys([]); }}
            allowClear={sectionFilter !== 'all'}
            onClear={() => { setSectionFilter('all'); setTablePage(1); setSelectedRowKeys([]); }}
            clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
            size="small"
            style={{ width: 130 }}
          >
            <Select.Option key="all" value="all">Section: All</Select.Option>
            {sectionOptions.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>

          <div style={{ flex: 1 }} />

          {canManage && selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Remove ${selectedRowKeys.length} selected child${selectedRowKeys.length !== 1 ? 'ren' : ''} from this event?`}
              onConfirm={() => bulkDeleteMutation.mutate(selectedRowKeys)}
              okText="Remove"
              okButtonProps={{ danger: true }}
              placement="bottomRight"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={bulkDeleteMutation.isPending}
                style={{ borderRadius: 9999, height: 36, fontWeight: 600 }}
              >
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}

          <Input
            prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)' }} />}
            placeholder="Search by name or phone…"
            value={childSearch}
            onChange={(e) => { setChildSearch(e.target.value); setTablePage(1); setSelectedRowKeys([]); }}
            allowClear
            size="small"
            style={{
              width:        220,
              borderRadius: 9999,
              borderColor:  'var(--wl-primary-pale)',
            }}
          />

          {canManage && isSchoolStaff && (
            <Button
              icon={<UploadOutlined />}
              onClick={() => setImportModalOpen(true)}
              style={{
                borderColor:  'var(--wl-primary)',
                color:        'var(--wl-primary)',
                borderRadius: 9999,
                height:       36,
                fontWeight:   600,
              }}
            >
              Import Excel
            </Button>
          )}
          {canManage && (
            <Dropdown
              menu={{ items: addMenuItems, onClick: handleAddMenu }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  background:   'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))',
                  border:       'none',
                  borderRadius: 9999,
                  fontWeight:   700,
                  paddingLeft:  20,
                  paddingRight: 20,
                  height:       36,
                }}
              >
                Add Child
              </Button>
            </Dropdown>
          )}
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <Empty
            description={
              <div>
                <div>
                  {childFilter === 'all'
                    ? 'No children registered yet.'
                    : `No ${childFilter} children.`}
                </div>
                {canManage && childFilter === 'all' && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Use "Add Child" to register children for this event.
                  </Text>
                )}
              </div>
            }
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Table
            dataSource={filteredRows}
            columns={columns}
            rowKey={(r) => r.id || r._id}
            loading={regLoading}
            size="small"
            rowSelection={canManage ? {
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
              getCheckboxProps: (r) => ({ disabled: r.isChecked }),
            } : undefined}
            pagination={{
              current:         tablePage,
              pageSize:        tablePageSize,
              total:           filteredRows.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal:       (total, range) => `${range[0]}–${range[1]} of ${total} children`,
              onChange:        (page, size) => { setTablePage(page); setTablePageSize(size); },
              style:           { padding: '10px 20px' },
            }}
            scroll={{ x: 700 }}
            style={{ padding: '0 4px' }}
          />
        )}
      </Card>

      <AddChildrenModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        eventId={eventId}
        event={event}
        defaultTab={addModalTab}
        onSuccess={handleAddSuccess}
      />

      <EventImportExcelModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        eventId={eventId}
      />

      <ChildForm
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditChild(null); }}
        onSubmit={(values) => updateChildMutation.mutateAsync({ id: editChild?.id || editChild?._id, data: values })}
        initialValues={editChild}
        loading={updateChildMutation.isPending}
      />
    </div>
  );
};

export default EventManage;
